import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, withKeys as _withKeys, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "overflow-hidden rounded-xl border border-[#E5E5E5] bg-[#FBFCFC]" }
const _hoisted_2 = { class: "flex items-center justify-between border-b border-[#E5E5E5] px-4 py-3" }
const _hoisted_3 = { class: "mt-0.5 text-sm font-semibold text-[#242B59]" }
const _hoisted_4 = { class: "rounded-full bg-[#48DDC3]/15 px-2.5 py-1 text-[10px] font-semibold text-[#168F7D]" }
const _hoisted_5 = { class: "overflow-x-auto" }
const _hoisted_6 = {
  key: 0,
  class: "min-w-[640px] text-left text-xs text-[#242B59]"
}
const _hoisted_7 = { class: "bg-white text-[#626468]" }
const _hoisted_8 = {
  key: 1,
  class: "min-w-[520px] text-left text-xs text-[#242B59]"
}
const _hoisted_9 = ["onMouseenter", "onFocus", "onClick", "onKeydown"]
const _hoisted_10 = {
  scope: "row",
  class: "max-w-[220px] truncate px-3 py-2 font-medium"
}
const _hoisted_11 = { class: "w-1/3 px-3 py-2" }
const _hoisted_12 = { class: "flex items-center gap-2" }
const _hoisted_13 = { class: "h-1.5 min-w-16 flex-1 overflow-hidden rounded-full bg-[#E5E5E5]" }
const _hoisted_14 = { class: "w-12 text-right text-[11px] font-medium text-[#626468]" }
const _hoisted_15 = {
  key: 0,
  class: "flex flex-col gap-2 border-t border-[#E5E5E5] px-3 py-2 text-xs text-[#626468] sm:flex-row sm:items-center sm:justify-between"
}
const _hoisted_16 = { class: "flex items-center gap-1" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = { class: "px-2" }
const _hoisted_19 = ["disabled"]

import { computed, ref, watch } from 'vue';
import {
    formatAuraDateLabel,
    formatAuraMetric,
    formatAuraStructuredTableValue,
} from '@/utils/auraPresentation';
import { getAuraChartColor } from '@/utils/auraChartPalette';

interface Props {
    labels: string[];
    data: unknown[];
    columns?: string[];
    tableRows?: Array<Record<string, unknown>>;
    unit?: string;
    selectedLabel?: string | null;
}

const rowsPerPage = 25;


export default /*@__PURE__*/_defineComponent({
  __name: 'AuraChartTable',
  props: {
    labels: {},
    data: {},
    columns: {},
    tableRows: {},
    unit: {},
    selectedLabel: {}
  },
  emits: ["select"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const activeLabel = ref<string | null>(null);
const currentPage = ref(1);
const isStructuredTable = computed(
    () => props.columns?.length && props.tableRows && props.tableRows.length > 0,
);
const structuredRows = computed(() => (isStructuredTable.value ? props.tableRows || [] : []));
const totalPages = computed(() =>
    Math.max(1, Math.ceil(structuredRows.value.length / rowsPerPage)),
);
const paginatedStructuredRows = computed(() => {
    const start = (currentPage.value - 1) * rowsPerPage;
    return structuredRows.value.slice(start, start + rowsPerPage);
});

watch(
    [structuredRows, totalPages],
    () => {
        if (currentPage.value > totalPages.value) currentPage.value = totalPages.value;
    },
    { immediate: true },
);

const rows = computed(() => {
    const numericValues = props.data.map((value) => (typeof value === 'number' ? value : 0));
    const total = numericValues.reduce((sum, value) => sum + Math.max(value, 0), 0);

    return props.labels.map((label, index) => {
        const rawValue = props.data[index];
        const value = typeof rawValue === 'number' || typeof rawValue === 'string' ? rawValue : '-';
        const numericValue = typeof rawValue === 'number' ? Math.max(rawValue, 0) : null;
        return {
            label,
            color: getAuraChartColor(index),
            value: formatAuraMetric(value, props.unit),
            percentage: numericValue !== null && total > 0 ? (numericValue / total) * 100 : null,
        };
    });
});

const selectAndActivate = (row: (typeof rows.value)[number]) => {
    activeLabel.value = row.label;
};

const selectRow = (row: (typeof rows.value)[number]) => {
    activeLabel.value = row.label;
    emit('select', row.label);
};

const formatColumnLabel = (column: string) => {
    const labels: Record<string, string> = {
        dispositivo: 'Dispositivo',
        fila: 'Fila',
        quantidade_de_usuarios: 'Quantidade de usuários',
        ramal: 'Ramal',
        usuario: 'Usuário',
    };
    return labels[column] || column.replaceAll('_', ' ');
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", null, [
        _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-[10px] font-semibold uppercase tracking-[0.16em] text-[#737373]" }, " Detalhamento ", -1)),
        _createElementVNode("p", _hoisted_3, _toDisplayString(isStructuredTable.value ? structuredRows.value.length : rows.value.length) + " " + _toDisplayString(isStructuredTable.value ? 'registros' : 'categorias'), 1)
      ]),
      _createElementVNode("span", _hoisted_4, _toDisplayString(isStructuredTable.value ? 'Lista detalhada' : 'Passe o mouse para explorar'), 1)
    ]),
    _createElementVNode("div", _hoisted_5, [
      (isStructuredTable.value)
        ? (_openBlock(), _createElementBlock("table", _hoisted_6, [
            _cache[4] || (_cache[4] = _createElementVNode("caption", { class: "sr-only" }, " Dados detalhados da consulta ", -1)),
            _createElementVNode("thead", _hoisted_7, [
              _createElementVNode("tr", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
                  return (_openBlock(), _createElementBlock("th", {
                    key: column,
                    scope: "col",
                    class: "px-3 py-2 font-semibold"
                  }, _toDisplayString(formatColumnLabel(column)), 1))
                }), 128))
              ])
            ]),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedStructuredRows.value, (row, index) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: `${currentPage.value}-${index}`,
                  class: "border-t border-[#E5E5E5]"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (column) => {
                    return (_openBlock(), _createElementBlock("td", {
                      key: column,
                      class: "px-3 py-2 text-[#242B59]"
                    }, _toDisplayString(_unref(formatAuraStructuredTableValue)(column, row[column])), 1))
                  }), 128))
                ]))
              }), 128))
            ])
          ]))
        : (_openBlock(), _createElementBlock("table", _hoisted_8, [
            _cache[5] || (_cache[5] = _createElementVNode("caption", { class: "sr-only" }, " Dados detalhados do gráfico ", -1)),
            _cache[6] || (_cache[6] = _createElementVNode("thead", { class: "bg-white text-[#626468]" }, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", {
                  scope: "col",
                  class: "px-3 py-2 font-semibold"
                }, "Categoria"),
                _createElementVNode("th", {
                  scope: "col",
                  class: "px-3 py-2 text-right font-semibold"
                }, "Valor"),
                _createElementVNode("th", {
                  scope: "col",
                  class: "px-3 py-2 text-right font-semibold"
                }, "Participação")
              ])
            ], -1)),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: row.label,
                  tabindex: "0",
                  class: _normalizeClass([
                            'group border-t border-[#E5E5E5] outline-none transition-all duration-200 hover:bg-[#48DDC3]/10 focus:bg-[#48DDC3]/10 focus:ring-2 focus:ring-inset focus:ring-[#48DDC3]',
                            activeLabel.value === row.label || props.selectedLabel === row.label
                                ? 'bg-[#48DDC3]/10'
                                : '',
                        ]),
                  onMouseenter: ($event: any) => (selectAndActivate(row)),
                  onFocus: ($event: any) => (selectAndActivate(row)),
                  onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (activeLabel.value = null)),
                  onClick: ($event: any) => (selectRow(row)),
                  onKeydown: _withKeys(($event: any) => (selectRow(row)), ["enter"])
                }, [
                  _createElementVNode("th", _hoisted_10, [
                    _createElementVNode("span", {
                      class: "mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle",
                      style: _normalizeStyle({ backgroundColor: row.color }),
                      "aria-hidden": "true"
                    }, null, 4),
                    _createTextVNode(" " + _toDisplayString(_unref(formatAuraDateLabel)(row.label)), 1)
                  ]),
                  _createElementVNode("td", {
                    class: "px-3 py-2 text-right font-semibold",
                    style: _normalizeStyle({ color: row.color })
                  }, _toDisplayString(row.value), 5),
                  _createElementVNode("td", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("div", {
                          class: "h-full rounded-full transition-all duration-300",
                          style: _normalizeStyle({
                                            width: `${row.percentage ?? 0}%`,
                                            backgroundColor: row.color,
                                        })
                        }, null, 4)
                      ]),
                      _createElementVNode("span", _hoisted_14, _toDisplayString(row.percentage === null
                                            ? '-'
                                            : `${row.percentage.toFixed(1)}%`), 1)
                    ])
                  ])
                ], 42, _hoisted_9))
              }), 128))
            ])
          ]))
    ]),
    (isStructuredTable.value && structuredRows.value.length > rowsPerPage)
      ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
          _createElementVNode("span", null, " Exibindo " + _toDisplayString((currentPage.value - 1) * rowsPerPage + 1) + " a " + _toDisplayString(Math.min(currentPage.value * rowsPerPage, structuredRows.value.length)) + " de " + _toDisplayString(structuredRows.value.length) + " registros ", 1),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("button", {
              type: "button",
              class: "rounded border border-[#E5E5E5] px-2 py-1 font-medium disabled:cursor-not-allowed disabled:opacity-40",
              disabled: currentPage.value === 1,
              "aria-label": "Página anterior",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (currentPage.value -= 1))
            }, " Anterior ", 8, _hoisted_17),
            _createElementVNode("span", _hoisted_18, _toDisplayString(currentPage.value) + " / " + _toDisplayString(totalPages.value), 1),
            _createElementVNode("button", {
              type: "button",
              class: "rounded border border-[#E5E5E5] px-2 py-1 font-medium disabled:cursor-not-allowed disabled:opacity-40",
              disabled: currentPage.value === totalPages.value,
              "aria-label": "Próxima página",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (currentPage.value += 1))
            }, " Próxima ", 8, _hoisted_19)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})